import { useSeoMeta, useHead } from '@unhead/react'
import { useState } from 'react'
import { ExportButtons } from '@/components/ExportButtons'
import { ExportData } from '@/utils/exportUtils'

function BagVolumeGuide() {
  useSeoMeta({
    title: '80 lb Bag of Concrete is How Many Cubic Feet? | Volume Guide',
    description: 'Learn how many cubic feet in an 80lb bag of concrete. Complete guide to concrete bag sizes: 40lb, 60lb, and 80lb bags with volume conversions and coverage charts.',
    ogTitle: '80 lb Bag of Concrete Volume | Cubic Feet Guide',
    ogDescription: 'How many cubic feet in an 80lb bag of concrete? Complete guide.',
    ogUrl: 'https://www.concrete-calculator.com/bag-volume-guide/',
    ogType: 'website',
    twitterCard: 'summary_large_image'
  })

  useHead({
    link: [
      { rel: 'canonical', href: 'https://www.concrete-calculator.com/bag-volume-guide/' }
    ]
  })

  const [volume, setVolume] = useState('')
  const [unit, setUnit] = useState('cubicFeet')
  const [results, setResults] = useState<{
    bags40lb: number
    bags60lb: number
    bags80lb: number
    cubicFeet: number
    cubicYards: number
  } | null>(null)

  const calculate = () => {
    const vol = parseFloat(volume)
    if (isNaN(vol) || vol <= 0) {
      return
    }

    // Convert to cubic feet
    let cubicFeet = vol
    if (unit === 'cubicYards') {
      cubicFeet = vol * 27
    }

    const cubicYards = cubicFeet / 27

    // Bag yields: 40lb = 0.3 ft³, 60lb = 0.45 ft³, 80lb = 0.6 ft³
    const bags40 = Math.ceil(cubicFeet / 0.3)
    const bags60 = Math.ceil(cubicFeet / 0.45)
    const bags80 = Math.ceil(cubicFeet / 0.6)

    setResults({
      bags40lb: bags40,
      bags60lb: bags60,
      bags80lb: bags80,
      cubicFeet: cubicFeet,
      cubicYards: cubicYards
    })
  }

  const getExportData = (): ExportData => ({
    title: 'Concrete Bag Volume Calculator Results',
    inputs: [
      { label: 'Volume Unit', value: unit === 'cubicFeet' ? 'Cubic Feet' : 'Cubic Yards' },
      { label: 'Volume Amount', value: volume }
    ],
    results: [
      { label: 'Volume (Cubic Feet)', value: `${results?.cubicFeet.toFixed(2)} ft³` },
      { label: 'Volume (Cubic Yards)', value: `${results?.cubicYards.toFixed(2)} yd³` },
      { label: '40 lb Bags', value: `${results?.bags40lb} bags` },
      { label: '60 lb Bags', value: `${results?.bags60lb} bags` },
      { label: '80 lb Bags', value: `${results?.bags80lb} bags` }
    ]
  })

  const reset = () => {
    setVolume('')
    setUnit('cubicFeet')
    setResults(null)
  }

  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 style={{ fontSize: '2rem', color: '#4A4A4A', marginBottom: '1rem' }}>
          80 lb Bag of Concrete is How Many Cubic Feet?
        </h1>
        
        <nav aria-label="Calculator Tools" className="mb-8">
          <a href="/" className="text-[#FF8C00] hover:underline">← Back to Home</a>
        </nav>

        {/* Quick Answer */}
        <div className="bg-[#FF8C00] text-white rounded-lg p-6 mb-8 shadow-lg">
          <h2 className="text-2xl font-bold mb-3">Quick Answer</h2>
          <p className="text-lg leading-relaxed">
            An <strong>80 lb bag of concrete</strong> yields approximately <strong>0.6 cubic feet</strong> when mixed. 
            This means you need about <strong>1.67 bags per cubic foot</strong>, or <strong>45 bags per cubic yard</strong>.
          </p>
        </div>

        {/* Bag Volume Reference Table */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6 md:p-8 mb-8 shadow-sm">
          <h2 className="text-xl font-bold text-[#4A4A4A] mb-6">Concrete Bag Volume Reference</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border-2 border-gray-300 px-4 py-3 text-left text-[#4A4A4A] font-bold">Bag Size</th>
                  <th className="border-2 border-gray-300 px-4 py-3 text-left text-[#4A4A4A] font-bold">Cubic Feet</th>
                  <th className="border-2 border-gray-300 px-4 py-3 text-left text-[#4A4A4A] font-bold">Bags per Cubic Yard</th>
                  <th className="border-2 border-gray-300 px-4 py-3 text-left text-[#4A4A4A] font-bold">Best For</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-2 border-gray-300 px-4 py-3 font-semibold">40 lb</td>
                  <td className="border-2 border-gray-300 px-4 py-3">0.3 ft³</td>
                  <td className="border-2 border-gray-300 px-4 py-3">90 bags</td>
                  <td className="border-2 border-gray-300 px-4 py-3">Small repairs, anchors</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border-2 border-gray-300 px-4 py-3 font-semibold">60 lb</td>
                  <td className="border-2 border-gray-300 px-4 py-3">0.45 ft³</td>
                  <td className="border-2 border-gray-300 px-4 py-3">60 bags</td>
                  <td className="border-2 border-gray-300 px-4 py-3">Medium projects, posts</td>
                </tr>
                <tr>
                  <td className="border-2 border-gray-300 px-4 py-3 font-semibold text-[#FF8C00]">80 lb</td>
                  <td className="border-2 border-gray-300 px-4 py-3 font-semibold text-[#FF8C00]">0.6 ft³</td>
                  <td className="border-2 border-gray-300 px-4 py-3 font-semibold text-[#FF8C00]">45 bags</td>
                  <td className="border-2 border-gray-300 px-4 py-3">Most projects, slabs</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Calculator */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6 md:p-8 mb-12 shadow-sm">
          <h2 className="text-xl font-bold text-[#4A4A4A] mb-6">Bag Calculator - Convert Volume to Bags</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="unit" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Volume Unit
              </label>
              <select
                id="unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
              >
                <option value="cubicFeet">Cubic Feet</option>
                <option value="cubicYards">Cubic Yards</option>
              </select>
            </div>

            <div>
              <label htmlFor="volume" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Volume Amount
              </label>
              <input
                id="volume"
                type="number"
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                placeholder="Enter volume"
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                min="0"
                step="0.1"
              />
            </div>
          </div>

          <div className="flex gap-3 mb-6">
            <button
              onClick={calculate}
              className="flex-1 bg-[#FF8C00] text-white font-bold py-3 px-6 rounded-md hover:bg-[#E67E00] transition-colors text-lg"
            >
              Calculate Bags
            </button>
            <button
              onClick={reset}
              className="flex-1 bg-gray-500 text-white font-bold py-3 px-6 rounded-md hover:bg-gray-600 transition-colors text-lg"
            >
              Reset
            </button>
          </div>

          {results && (
            <div className="bg-gray-50 border-2 border-[#FF8C00] rounded-lg p-6">
              <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
                <h3 className="text-xl font-bold text-[#4A4A4A]">Bags Needed</h3>
                <ExportButtons data={getExportData()} />
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-white p-4 rounded border border-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">40 lb Bags</p>
                      <p className="text-xs text-gray-500">0.3 ft³ per bag</p>
                    </div>
                    <p className="text-3xl font-bold text-[#4A4A4A]">{results.bags40lb}</p>
                  </div>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">60 lb Bags</p>
                      <p className="text-xs text-gray-500">0.45 ft³ per bag</p>
                    </div>
                    <p className="text-3xl font-bold text-[#4A4A4A]">{results.bags60lb}</p>
                  </div>
                </div>
                <div className="bg-[#FF8C00] p-4 rounded border-2 border-[#FF8C00]">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-white mb-1 font-semibold">80 lb Bags (Most Common)</p>
                      <p className="text-xs text-white/80">0.6 ft³ per bag</p>
                    </div>
                    <p className="text-3xl font-bold text-white">{results.bags80lb}</p>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded border border-blue-200">
                <p className="text-sm text-gray-700">
                  <strong>Volume Reference:</strong> {results.cubicFeet.toFixed(2)} cubic feet = {results.cubicYards.toFixed(2)} cubic yards
                </p>
              </div>
            </div>
          )}

          <div className="mt-6 text-sm text-gray-600">
            <p className="mb-2">
              <strong>How to use:</strong> Enter your project volume to see how many bags you need in different sizes. The 80lb bag is the most commonly used size for DIY and professional projects.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <section aria-labelledby="faq-heading" className="mt-12">
          <h2 id="faq-heading" className="text-2xl font-bold text-[#4A4A4A] mb-4">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                How many cubic feet in an 80 lb bag of concrete?
              </h3>
              <p className="text-gray-700">
                An 80 lb bag of concrete yields approximately 0.6 cubic feet when mixed. This is based on standard concrete mix ratios and includes the water added during mixing.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                How many 80lb bags of concrete make a yard?
              </h3>
              <p className="text-gray-700">
                You need approximately 45 bags of 80lb concrete to make one cubic yard (27 cubic feet). At 0.6 cubic feet per bag: 27 ÷ 0.6 = 45 bags.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                Should I buy 60lb or 80lb bags?
              </h3>
              <p className="text-gray-700">
                80lb bags are more cost-effective for large projects and require fewer trips. 60lb bags are easier to lift and handle, making them better for small repairs or if you have physical limitations. 60lb bags cost slightly more per cubic foot.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default BagVolumeGuide
